import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { N0DES_API_PREFIX } from "@/api/domain"
import { Tag } from "@/api/tags"
import type {
  ChangePasswordRequest,
  ResetPasswordRequest,
  User,
  UserUpdates,
  VerifyEmailRequest,
  VerifyEmailResponse,
} from "@/types"

export type UserIdArg = {
  user_id: string
}

export type AuthStatus = {
  authenticated: boolean
}

export type UserAuthSignup = {
  password: string
  username: string
  name: string
  email: string
  // This is passed as `redirect` query param to the email verification email if set.
  redirect_token?: string
}

export const usersApi = createApi({
  reducerPath: "usersApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${N0DES_API_PREFIX}`,
    mode: "cors",
    credentials: "include",
  }),
  tagTypes: [Tag.AuthStatus, Tag.CurrentUser],
  endpoints: (build) => ({
    authStatusGet: build.query<AuthStatus, void>({
      query: () => ({
        url: "user/auth_status",
      }),
      providesTags: [Tag.AuthStatus],
    }),
    userGet: build.query<User, UserIdArg>({
      query: ({ user_id }) => ({
        url: `users/${user_id}`,
      }),
    }),

    userPostSignup: build.mutation<User, UserAuthSignup>({
      query: (credentials) => ({
        url: "user/signup",
        method: "POST",
        body: credentials,
      }),
    }),
    userPostLogin: build.mutation<
      User,
      { name_or_email: string; password: string }
    >({
      query: (credentials) => ({
        url: "user/login",
        method: "POST",
        body: credentials,
      }),
      invalidatesTags: [Tag.AuthStatus, Tag.CurrentUser],
    }),
    userPostLogout: build.mutation<User, void>({
      query: () => ({
        url: "user/logout",
        method: "POST",
      }),
      invalidatesTags: [Tag.AuthStatus, Tag.CurrentUser],
    }),
    userPostForgot: build.mutation<void, { email: string }>({
      query: (body) => ({
        url: "user/forgot",
        method: "POST",
        body,
      }),
    }),
    userPostReset: build.mutation<void, ResetPasswordRequest>({
      query: (body) => ({
        url: "user/reset",
        method: "POST",
        body,
      }),
    }),
    userPostVerifyEmail: build.mutation<
      VerifyEmailResponse,
      VerifyEmailRequest
    >({
      query: (body) => ({
        url: "user/verify-email",
        method: "POST",
        body,
      }),
    }),
    userPostSendVerifyEmail: build.mutation<void, void>({
      query: () => ({
        url: "user/send-verify-email",
        method: "POST",
      }),
    }),
    userGetMe: build.query<User, void>({
      query: () => ({ url: "user/me", mode: "cors", credentials: "include" }),
      providesTags: [Tag.CurrentUser],
    }),
    userPutMe: build.mutation<User, UserUpdates>({
      query: (body) => ({
        url: "user/me",
        method: "PUT",
        body,
      }),
      invalidatesTags: [Tag.CurrentUser],
    }),
    userPostChangePassword: build.mutation<void, ChangePasswordRequest>({
      query: (body) => ({
        url: "user/change-password",
        method: "POST",
        body,
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useAuthStatusGetQuery,
  useUserGetQuery,
  useUserPostSignupMutation,
  useUserPostLoginMutation,
  useUserPostLogoutMutation,
  useUserPostForgotMutation,
  useUserPostResetMutation,
  useUserPostVerifyEmailMutation,
  useUserPostSendVerifyEmailMutation,
  useUserGetMeQuery,
  useUserPutMeMutation,
  useUserPostChangePasswordMutation,
} = usersApi
